<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { DxList } from 'devextreme-vue/list'

import { useWebSiteStore } from '@/stores/web-site.store'

import { useODataSource } from '@/composables/data-grid'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { useEntityChanges } from '@/composables/entity-changes'
import { useDetectDevice } from '@/composables/device'
import { useHighlightNewRow } from '@/composables/scroll'

import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import ProjectListItem from '@/components/shared/ProjectListItem.vue'
import ProjectEdit from '@/components/shared/ProjectEdit.vue'
import ProjectDetail from '@/components/shared/ProjectDetail.vue'
import SignalBar from '@/components/shared/signal-bar/SignalBar.vue'

import { usePlanningStore } from '@/stores/planning.store'

const { isMobile } = useDetectDevice()

const route = useRoute()
const webSiteStore = useWebSiteStore()
const planningStore = usePlanningStore()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()

const listContainer = ref(null)
let listInstance = null

const editItem = ref(null)
const detailItem = ref(null)
const isEmptyDataSource = ref(false)
const quickCreate = ref(false)

const onAddClick = (quick = false) => { quickCreate.value = quick === true; editItem.value = {} }

webSiteStore.fabActions = [
  {
    id: 'add',
    icon: 'mdi-briefcase-plus-outline',
    onClick: () => onAddClick(false),
  },
  {
    id: 'quick-create',
    icon: 'mdi-flash',
    onClick: () => onAddClick(true),
  },
]

const dataSource = useODataSource(planningStore.api.getAbsoluteUrl() + planningStore.api.projectsUrl, {
  onChanged: () => {
    isEmptyDataSource.value = dataSource.totalCount() === 0
  },
  searchExpr: 'search_field',
})

const onProjectClick = (item) => { detailItem.value = item }

const onChange = async () => {
  editItem.value = null
  detailItem.value = null
  dataSource.reload()
}

const onProjectCreated = async () => {
  editItem.value = null
  webSiteStore.activateUpdatedDateSort(route.name)
  dataSource.sort(webSiteStore.buildSortOrderBy(route.name))
  dataSource.pageIndex(0)
  const loaded = await dataSource.reload()
  await highlightRowAsync(loaded?.[0]?.id, listContainer)
  listInstance?.repaint()
}

onMounted(async () => {
  if (webSiteStore.editedProjectId !== null) {
    if (webSiteStore.editedProjectId === 0) {
      editItem.value = {}
    } else {
      detailItem.value = (await planningStore.fetchProjectByIdAsync(webSiteStore.editedProjectId)).data
    }
    webSiteStore.editedProjectId = null
  }
})


useEntityChanges([entityChangeTypeEnum.PROJECT], (change) => (change.created ? onProjectCreated() : dataSource.reload()), () => editItem.value !== null)
</script>
<template>
  <div>
    <filter-sort-selector
      :data-source="dataSource"
      :kpi-enabled="true"
      :kpi-visible="webSiteStore.isKpiBarVisible('projects')"
      @toggle-kpi="webSiteStore.toggleKpiBar('projects')"
    />
    <signal-bar page="projects" embedded :visible="webSiteStore.isKpiBarVisible('projects')" />
    <q-card flat>
      <q-card-section ref="listContainer" class="q-pa-none">
        <dx-list
          v-show="!isEmptyDataSource"
          :data-source="dataSource"
          height="calc(100vh - 8rem)"
          page-load-mode="scrollBottom"
          class="events-list"
          item-template="item"
          selection-mode="none"
          :active-state-enabled="false"
          @initialized="e => listInstance = e.component"
        >
          <template #item="{ data: project }">
            <q-item>
              <project-list-item
                :key="project.id"
                v-ripple
                class="q-pa-sm"
                dense
                clickable
                :item="project"
                show-task-progress
                :class="{ 'row-highlight': project.id === highlightedRowId }"
                :disabled="project.archived"
                @click="onProjectClick"
              />
            </q-item>
          </template>
        </dx-list>
        <q-dialog :model-value="editItem !== null" :maximized="isMobile" class="dialog-xs">
          <project-edit
            v-if="editItem !== null"
            :model-value="editItem"
            :initial-quick-create="quickCreate"
            @update="onProjectCreated"
            @cancel="onChange"
            @delete="onChange"
          />
        </q-dialog>
        <q-dialog :model-value="detailItem !== null" :maximized="isMobile" class="dialog-xs">
          <project-detail
            v-if="detailItem !== null"
            :model-value="detailItem"
            @update="onChange"
            @cancel="onChange"
          />
        </q-dialog>
      </q-card-section>
    </q-card>
  </div>
</template>
